import { BrandLogo, Button, Card, CardContent, Input, Progress } from '@bs/ui-components'
import { PhoneVerifyDialog } from '@/features/account-settings/phone-verify-dialog'
import { ArrowLeft, ArrowRight, Check, Upload } from 'lucide-react'
import { useEffect, useRef, useState, type ChangeEvent, type ReactNode } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { useAuth } from '@/app/use-auth'
import { getMyAccount, updateMyAccount } from '@/services/account'
import { getMyProfile, updateMyProfile, type UpdateTalentProfileInput } from '@/features/profile/api'
import { AVAILABILITY_TO_STATUS, STATUS_TO_AVAILABILITY, getMyJobPreferences, updateMyJobPreferences, type UpdateJobPreferencesInput } from '@/services/job-preferences'
import { uploadResume } from '@/services/resumes'
import { applyToJob, getJobBySlug } from '@/features/jobs/api'
import { getCities, getIndustries, getLookup, getRoles, getSkills, toSelectOptions, type CityItem, type MetadataItem } from '@/services/metadata'
import { JobPreferencesEditor, type JobPreferencesOptions } from '@/features/profile/job-preferences-editor'
import { ProfileSkillsEditor } from '@/features/profile/profile-skills-editor'
import { SearchPicker, type SearchPickerOption } from '@/components/ui/search-picker'
import { availabilityOptions, type AvailabilityId, type JobPreferencesView, type ProfileSkillView } from '@/features/profile/types'

/**
 * Same data-quality workaround `profile-page.tsx`'s `Preferences` uses on the same lookup
 * category, kept in sync there — see that file's own comment for the seeded junk rows this
 * filters out. Duplicated rather than shared: two call sites, and the alternative is a shared
 * module for six lines.
 */
const SENIORITY_ALLOWLIST = [
  'Intern', 'Junior', 'Experienced', 'Senior', 'Team Leader', 'Manager',
  'Director', 'CxO', 'Consultant', 'Owner', 'Founder', 'VP',
]

/**
 * The `languages` lookup's values are ISO-639-1 codes ("en", "he"), not display names — resolved
 * for display via `Intl.DisplayNames`, exactly like `profile-page.tsx`'s Language editor (see its
 * comment). Duplicated rather than shared for the same reason `SENIORITY_ALLOWLIST` is: two call
 * sites, and the alternative is a shared module for six lines.
 */
const languageDisplayNames = typeof Intl !== 'undefined' && 'DisplayNames' in Intl ? new Intl.DisplayNames(['en'], { type: 'language' }) : null
function languageDisplayName(code: string): string {
  try {
    return languageDisplayNames?.of(code) ?? code
  } catch {
    return code
  }
}

const emptyPreferences: JobPreferencesView = {
  workspaceTypes: [],
  seniorityLevels: [],
  jobTitles: [],
  skills: [],
  jobTypes: [],
  desiredSalary: '',
  currency: '',
  desiredLocations: [],
  companySizes: [],
  industries: [],
}

interface OnboardingDraft {
  firstName: string
  lastName: string
  email: string
  password: string
  phone: string
  resumeFile: File | null
  availability: AvailabilityId
  preferences: JobPreferencesView
  /** `bs-talent.profiles.languages` entries — elId + level, the exact shape PATCH /v1/profile takes. */
  languages: Array<{ language: number; level: number }>
  linkedIn: string
  aboutMe: string
  profileSkills: ProfileSkillView[]
}

/**
 * The ONLY thing localStorage holds now is the step INDEX. Every answer writes to the server the
 * moment its step completes (epic-10.2: each step is a matching input) — the server is the draft,
 * so a reload rehydrates from real data and an abandoned wizard still left usable preferences
 * behind. The two earlier keys persisted the whole draft client-side (the first one password
 * included, in plain text); both are retired here so no copy survives on real users' machines.
 */
const STEP_KEY = 'bs-talent-onboarding-step'
if (typeof window !== 'undefined') {
  window.localStorage.removeItem('bs-talent-mock-onboarding')
  window.localStorage.removeItem('bs-talent-onboarding')
}
const initialDraft: OnboardingDraft = {
  firstName: '',
  lastName: '',
  email: '',
  password: '',
  phone: '',
  resumeFile: null,
  availability: 1,
  preferences: emptyPreferences,
  languages: [],
  linkedIn: '',
  aboutMe: '',
  profileSkills: [],
}

// Languages sits after the preference steps and Enhance profile right before Complete — the
// spec's order (docs/features/talent-fe.md "15 steps": …Company size…Location, Languages, …,
// Enhance profile, Finish), mapped onto this wizard's consolidated preference steps.
const steps = ['Basic info', 'Resume', 'About you', 'About the job', 'About the company', 'Languages', 'LinkedIn', 'Enhance profile', 'Complete'] as const

export function OnboardingPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { isAuthenticated, signUp } = useAuth()
  const appliedJobSlug = searchParams.get('job_slug')
  const [step, setStep] = useState(readInitialStep())
  const [draft, setDraft] = useState<OnboardingDraft>(initialDraft)
  // A resume already on the profile (this or an earlier session) satisfies the resume step
  // without re-uploading — the wizard resumes from server truth, not from a stored File.
  const [existingResumeName, setExistingResumeName] = useState('')
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const progress = Math.round((step / (steps.length - 1)) * 100)

  const [meta, setMeta] = useState<{
    workspaceType: MetadataItem[]
    seniorityLevel: MetadataItem[]
    jobType: MetadataItem[]
    currency: MetadataItem[]
    role: MetadataItem[]
    skill: MetadataItem[]
    industry: MetadataItem[]
    companySize: MetadataItem[]
    language: MetadataItem[]
    city: CityItem[]
  } | null>(null)
  const [metaError, setMetaError] = useState(false)
  // Bumped by the retry button — re-keys the effect below so a failed vocabulary load can be
  // re-attempted without reloading the whole wizard.
  const [metaAttempt, setMetaAttempt] = useState(0)

  useEffect(() => {
    // `/v1/metadata/*` requires a session (measured: 401s for an anonymous caller) — this effect
    // must wait for `isAuthenticated`, or it fires once on mount while the
    // account doesn't exist yet and never retries once `next()` creates one.
    if (!isAuthenticated) return
    let cancelled = false
    Promise.all([
      getLookup('workspaceTypes'),
      getLookup('seniorityLevels'),
      getLookup('positionTypes'),
      getLookup('currency'),
      getRoles(),
      getSkills(),
      getIndustries(),
      getLookup('companySizes'),
      getLookup('languages'),
    ])
      .then(([workspaceType, seniorityLevelRaw, jobType, currency, role, skill, industry, companySize, language]) => {
        if (cancelled) return
        setMeta({
          workspaceType,
          seniorityLevel: seniorityLevelRaw.filter((item) => SENIORITY_ALLOWLIST.includes(item.value)),
          jobType,
          currency,
          role,
          skill,
          industry,
          companySize,
          language,
          city: [],
        })
      })
      .catch(() => {
        // Without `meta` every picker from step 2 on is empty and the steps' writes cannot
        // translate labels back to elIds — the wizard is unusable, so say so and offer a retry
        // instead of presenting blank option lists as if that's all there is.
        if (!cancelled) setMetaError(true)
      })
    return () => {
      cancelled = true
    }
  }, [isAuthenticated, metaAttempt])

  useEffect(() => {
    window.localStorage.setItem(STEP_KEY, String(step))
  }, [step])

  /**
   * Server → draft. The server IS the draft (each completed step already wrote there), so a
   * reload mid-wizard resumes from real data instead of a client-side copy. Runs once per
   * session, after `meta` loads — hydration translates elIds back to the labels the pickers
   * render, which needs the vocabulary in hand. Cities resolve through `getCities({ids})` and
   * merge into the picker's known set so the chips render with names, not numbers.
   */
  const hydratedRef = useRef(false)
  useEffect(() => {
    if (!isAuthenticated || !meta || hydratedRef.current) return
    hydratedRef.current = true
    const idsToLabels = (items: MetadataItem[], ids: number[]) =>
      ids.flatMap((id) => {
        const item = items.find((candidate) => candidate.elId === id)
        return item ? [item.value] : []
      })
    void Promise.all([
      getMyJobPreferences().catch(() => null),
      getMyProfile().catch(() => null),
      getMyAccount().catch(() => null),
    ]).then(async ([preferencesResult, profileResult, accountResult]) => {
      const preferences = preferencesResult?.preferences
      const cities = preferences?.desiredCityIds.length
        ? await getCities({ ids: preferences.desiredCityIds }).catch(() => [])
        : []
      if (cities.length) mergeCities(cities.map((city) => ({ id: String(city.cityId), label: city.city })))
      const selectedCv = profileResult?.profile.cvs.find((cv) => cv.isSelected) ?? profileResult?.profile.cvs.at(-1)
      if (selectedCv) setExistingResumeName(selectedCv.displayName || selectedCv.fileName || 'Uploaded resume')
      setDraft((current) => ({
        ...current,
        phone: current.phone || (accountResult?.user.phone ?? ''),
        linkedIn: current.linkedIn || (profileResult?.profile.links?.linkedin ?? ''),
        aboutMe: current.aboutMe || (profileResult?.profile.aboutMe ?? ''),
        languages: current.languages.length
          ? current.languages
          : (profileResult?.profile.languages ?? []).map((entry) => ({ language: entry.language, level: entry.level })),
        profileSkills: current.profileSkills.length
          ? current.profileSkills
          : (profileResult?.profile.skills ?? []).map((skill) => ({ skillId: skill.skillId ?? null, skillName: skill.skillName ?? '' })),
        ...(preferences
          ? {
              availability: STATUS_TO_AVAILABILITY[preferences.status],
              preferences: {
                ...current.preferences,
                workspaceTypes: idsToLabels(meta.workspaceType, preferences.workspaceType),
                seniorityLevels: idsToLabels(meta.seniorityLevel, preferences.seniorityLevel),
                jobTitles: idsToLabels(meta.role, preferences.desiredRoles),
                jobTypes: idsToLabels(meta.jobType, preferences.positionTypes),
                skills: preferences.skills.flatMap((id) => {
                  const item = meta.skill.find((candidate) => candidate.elId === id)
                  return item ? [{ skillId: String(item.elId), skillName: item.value }] : []
                }),
                desiredSalary: preferences.desiredSalary ? String(preferences.desiredSalary) : current.preferences.desiredSalary,
                desiredLocations: cities.map((city) => city.city),
                industries: idsToLabels(meta.industry, preferences.industries),
                companySizes: idsToLabels(meta.companySize, preferences.companySizes),
              },
            }
          : {}),
      }))
    })
    // Deliberately keyed on [isAuthenticated, meta] only — mergeCities/setDraft are stable
    // setters, and hydratedRef already guarantees one run.
  }, [isAuthenticated, meta])

  const labelToId = (items: MetadataItem[], label: string) => items.find((item) => item.value === label)?.elId
  const cityLabelToId = (label: string) => meta?.city.find((c) => c.city === label)?.cityId

  function mergeCities(options: SearchPickerOption[]) {
    setMeta((current) => {
      if (!current) return current
      const known = new Map(current.city.map((c) => [c.cityId, c]))
      for (const option of options) known.set(Number(option.id), { cityId: Number(option.id), city: option.label })
      return { ...current, city: Array.from(known.values()) }
    })
  }

  const options: JobPreferencesOptions = meta
    ? {
        workspaceType: toSelectOptions(meta.workspaceType),
        seniorityLevel: toSelectOptions(meta.seniorityLevel),
        jobTitle: toSelectOptions(meta.role),
        skill: toSelectOptions(meta.skill),
        jobType: toSelectOptions(meta.jobType),
        location: [],
        companySize: toSelectOptions(meta.companySize),
        industry: toSelectOptions(meta.industry),
        currency: toSelectOptions(meta.currency),
      }
    : {
        // Vocabulary not loaded yet — empty pickers, never invented options. A fixture option's
        // value maps to no elId, so choosing one wrote garbage the server could not resolve.
        workspaceType: [], seniorityLevel: [], jobTitle: [], skill: [], jobType: [],
        location: [], companySize: [], industry: [], currency: [],
      }

  const languageOptions: SearchPickerOption[] = meta
    ? meta.language.map((item) => ({ id: String(item.elId), label: languageDisplayName(item.value) }))
    : []
  // `_id`, not `elId` — profile skills reference `Skill._id` directly (see metadata.ts), same
  // mapping `profile-page.tsx` feeds its own ProfileSkillsEditor.
  const skillSuggestions: ProfileSkillView[] = meta
    ? meta.skill.map((item) => ({ skillId: item._id ?? null, skillName: item.value }))
    : []

  async function next() {
    const validationError = validateStep(step, draft, { isAuthenticated, hasServerResume: Boolean(existingResumeName) })
    if (validationError) {
      setError(validationError)
      return
    }
    setError('')

    // The account is created here, leaving step 0, rather than at the final "finish" — every
    // step after this one shows REAL role/skill/lookup vocabulary (`/v1/metadata/*` requires a
    // session — measured, not assumed: it 401s for an anonymous caller), so those steps can only
    // render real options once a session actually exists. `!isAuthenticated` guards a Back-then-
    // Continue round trip from calling signup a second time with an email that's now its own.
    // Every step WRITES on leaving it (epic-10.2: each step is a matching input). The server is
    // the draft: an abandoned wizard still left usable preferences behind, and a reload
    // rehydrates from data instead of a client-side copy. A failed write keeps the user on the
    // step — nothing earlier is lost, it is already on the server.
    setSubmitting(true)
    try {
      if (step === 0) {
        if (!isAuthenticated) {
          await signUp(draft.email, draft.password, { first: draft.firstName, last: draft.lastName })
        }
        if (draft.phone.trim()) await updateMyAccount({ phone: draft.phone })
      }
      if (step === 1 && draft.resumeFile) {
        await uploadResume(draft.resumeFile)
        setExistingResumeName(draft.resumeFile.name)
      }
      if (step === 2) await updateMyJobPreferences(buildAboutYouPatch())
      if (step === 3) await updateMyJobPreferences(buildAboutJobPatch())
      if (step === 4) await updateMyJobPreferences(buildAboutCompanyPatch())
      if (step === 5) await updateMyProfile({ languages: draft.languages })
      if (step === 6 && draft.linkedIn.trim()) {
        await updateMyProfile({ links: { linkedin: draft.linkedIn } })
      }
      if (step === 7) {
        const enhancePatch = buildEnhanceProfilePatch()
        if (Object.keys(enhancePatch).length) await updateMyProfile(enhancePatch)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong saving this step. Please try again.')
      return
    } finally {
      setSubmitting(false)
    }

    setStep((current) => Math.min(steps.length - 1, current + 1))
  }

  /** About-you (step 2): the availability status plus everything the pickers on that step own. */
  function buildAboutYouPatch(): UpdateJobPreferencesInput {
    return {
      status: AVAILABILITY_TO_STATUS[draft.availability],
      workspaceType: meta ? draft.preferences.workspaceTypes.flatMap((label) => {
        const id = labelToId(meta.workspaceType, label)
        return id !== undefined ? [id] : []
      }) : [],
      seniorityLevel: meta ? draft.preferences.seniorityLevels.flatMap((label) => {
        const id = labelToId(meta.seniorityLevel, label)
        return id !== undefined ? [id] : []
      }) : [],
      desiredRoles: meta ? draft.preferences.jobTitles.flatMap((label) => {
        const id = labelToId(meta.role, label)
        return id !== undefined ? [id] : []
      }) : [],
      skills: draft.preferences.skills.flatMap((skill) => {
        const id = Number(skill.skillId)
        return Number.isFinite(id) ? [id] : []
      }),
    }
  }

  /** About-the-company (step 4): size + industry preferences. Both optional — legacy never
   *  required them, and an empty pick writes an empty list, which IS an answer. */
  function buildAboutCompanyPatch(): UpdateJobPreferencesInput {
    return {
      companySizes: meta ? draft.preferences.companySizes.flatMap((label) => {
        const id = labelToId(meta.companySize, label)
        return id !== undefined ? [id] : []
      }) : [],
      industries: meta ? draft.preferences.industries.flatMap((label) => {
        const id = labelToId(meta.industry, label)
        return id !== undefined ? [id] : []
      }) : [],
    }
  }

  /**
   * Enhance-profile (step 7): optional enrichment (the spec's own words), so only what the
   * candidate actually filled in goes to the server. An empty patch means the step was skipped —
   * writing `aboutMe: ''` / `skills: []` there would CLEAR profile data an earlier session (or CV
   * parsing) already put on the record, rather than record an answer.
   */
  function buildEnhanceProfilePatch(): UpdateTalentProfileInput {
    const aboutMe = draft.aboutMe.trim()
    const skills = draft.profileSkills
      .filter((skill) => skill.skillName.trim())
      .map((skill) => ({ skillName: skill.skillName.trim(), ...(skill.skillId ? { skillId: skill.skillId } : {}) }))
    return {
      ...(aboutMe ? { aboutMe } : {}),
      ...(skills.length ? { skills } : {}),
    }
  }

  /** About-the-job (step 3): job types, salary, locations — nothing step 2 already owns. */
  function buildAboutJobPatch(): UpdateJobPreferencesInput {
    return {
      positionTypes: meta ? draft.preferences.jobTypes.flatMap((label) => {
        const id = labelToId(meta.jobType, label)
        return id !== undefined ? [id] : []
      }) : [],
      ...(draft.preferences.desiredSalary.trim() ? { desiredSalary: Number(draft.preferences.desiredSalary) } : {}),
      desiredCityIds: draft.preferences.desiredLocations.flatMap((label) => {
        const id = cityLabelToId(label)
        return id !== undefined ? [id] : []
      }),
    }
  }

  function back() {
    setError('')
    setStep((current) => Math.max(0, current - 1))
  }

  async function finish() {
    setError('')
    setSubmitting(true)
    try {
      // Every answer already went to the server as its step completed — the only work left is
      // the deep-linked application (arriving via /apply?job_slug=...) and leaving the wizard.
      if (appliedJobSlug) {
        const job = await getJobBySlug(appliedJobSlug).catch(() => null)
        if (job) {
          await applyToJob({
            jobId: job._id,
            companyId: job.companyId,
            jobData: { title: job.title, slug: job.slug },
          }).catch(() => {})
        }
      }
      window.localStorage.removeItem(STEP_KEY)
      navigate('/my-profile?tab=preferences')
    } finally {
      setSubmitting(false)
    }
  }

  function reset() {
    window.localStorage.removeItem(STEP_KEY)
    setDraft(initialDraft)
    setStep(0)
    setError('')
  }

  function selectFile(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0]
    if (!file) return
    setDraft((current) => ({ ...current, resumeFile: file }))
    setError('')
    event.target.value = ''
  }

  return (
    <div className="min-h-screen bg-[#f8f8f8]">
      <header className="flex h-[82px] items-center border-b border-border bg-white px-5 sm:px-8">
        <BrandLogo />
        <button type="button" onClick={reset} className="ml-auto mr-5 text-sm font-medium text-muted-foreground hover:text-accent">Start over</button>
        <span className="text-sm font-medium text-muted-foreground">Step {step + 1} of {steps.length}</span>
      </header>

      <main className="mx-auto grid min-h-[calc(100vh-82px)] max-w-6xl lg:grid-cols-[300px_1fr]">
        <aside className="hidden bg-primary p-8 text-white lg:block">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#00c8df]">Your profile</p>
          <h2 className="mt-4 text-2xl font-semibold">Tell us what works for you.</h2>
          <p className="mt-3 text-sm leading-6 text-white/65">Every answer improves the opportunities we recommend.</p>
          <div className="mt-10"><div className="mb-2 flex justify-between text-xs text-white/70"><span>Progress</span><span>{progress}%</span></div><Progress value={progress} /></div>
          <ol className="mt-10 space-y-3 text-sm">
            {steps.map((item, index) => <li key={item} className={`flex items-center gap-3 ${index <= step ? 'text-white' : 'text-white/40'}`}><span className={`flex size-7 shrink-0 items-center justify-center rounded-full text-xs ${index < step ? 'bg-[#00c8df] text-primary' : index === step ? 'border-2 border-[#00c8df]' : 'border border-white/25'}`}>{index < step ? <Check className="size-4" /> : index + 1}</span><span className="truncate">{item}</span></li>)}
          </ol>
        </aside>

        <section className="flex items-center justify-center p-4 sm:p-8 lg:p-12">
          <Card className="w-full max-w-2xl rounded-[20px]">
            <CardContent className="p-6 sm:p-10">
              <StepContent
                step={step}
                draft={draft}
                setDraft={setDraft}
                onChooseFile={() => fileInputRef.current?.click()}
                options={options}
                languageOptions={languageOptions}
                skillSuggestions={skillSuggestions}
                onLocationResultsSeen={mergeCities}
                existingResumeName={existingResumeName}
              />
              <input ref={fileInputRef} type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={selectFile} />
              {metaError && !meta ? (
                <div role="alert" className="mt-5 flex flex-wrap items-center justify-between gap-3 rounded-xl bg-destructive/5 px-4 py-3 text-sm text-destructive">
                  <span>Could not load the form options (roles, skills, locations) — the next steps need them.</span>
                  <Button type="button" size="sm" variant="outline" onClick={() => { setMetaError(false); setMetaAttempt((current) => current + 1) }}>Try again</Button>
                </div>
              ) : null}
              {error ? <p role="alert" className="mt-5 rounded-xl bg-destructive/5 px-4 py-3 text-sm text-destructive">{error}</p> : null}
              <div className="mt-9 flex items-center justify-between border-t border-border pt-6">
                <Button type="button" variant="ghost" onClick={back} disabled={step === 0 || submitting}><ArrowLeft className="size-4" /> Back</Button>
                {step === steps.length - 1
                  ? <Button type="button" onClick={() => void finish()} disabled={submitting}>{submitting ? 'Building your profile…' : <>Build my free profile <Check className="size-4" /></>}</Button>
                  : <Button type="button" onClick={() => void next()} disabled={submitting}>{submitting ? 'Creating your account…' : <>Continue <ArrowRight className="size-4" /></>}</Button>}
              </div>
            </CardContent>
          </Card>
        </section>
      </main>
    </div>
  )
}


function StepContent({
  step, draft, setDraft, onChooseFile, options, languageOptions, skillSuggestions, onLocationResultsSeen, existingResumeName,
}: {
  step: number
  draft: OnboardingDraft
  setDraft(value: OnboardingDraft | ((current: OnboardingDraft) => OnboardingDraft)): void
  onChooseFile(): void
  options: JobPreferencesOptions
  languageOptions: SearchPickerOption[]
  skillSuggestions: ProfileSkillView[]
  onLocationResultsSeen(cities: SearchPickerOption[]): void
  /** Resume already on the profile — a resumed session shows it instead of demanding a re-upload. */
  existingResumeName: string
}) {
  const update = <K extends keyof OnboardingDraft>(key: K, value: OnboardingDraft[K]) => setDraft((current) => ({ ...current, [key]: value }))
  // Legacy's Twilio Verify step — local to the step renderer, it only matters on step 1.
  const [verifyPhoneOpen, setVerifyPhoneOpen] = useState(false)
  const [phoneVerified, setPhoneVerified] = useState(false)
  const updatePreferences = (next: JobPreferencesView) => setDraft((current) => ({ ...current, preferences: next }))
  const languageLabels = (entries: OnboardingDraft['languages']) =>
    entries.flatMap((entry) => {
      const option = languageOptions.find((candidate) => candidate.id === String(entry.language))
      return option ? [option.label] : []
    })

  if (step === 0) return <Step title="First, tell us about yourself" description="This information will appear on your talent profile."><div className="mt-7 grid gap-4 sm:grid-cols-2"><OnboardingField label="First name"><Input value={draft.firstName} onChange={(event) => update('firstName', event.target.value)} placeholder="Enter first name" /></OnboardingField><OnboardingField label="Last name"><Input value={draft.lastName} onChange={(event) => update('lastName', event.target.value)} placeholder="Enter last name" /></OnboardingField><OnboardingField label="Email"><Input type="email" value={draft.email} onChange={(event) => update('email', event.target.value)} placeholder="name@example.com" /></OnboardingField><OnboardingField label="Phone"><Input type="tel" value={draft.phone} onChange={(event) => update('phone', event.target.value)} placeholder="Enter phone number" /></OnboardingField><OnboardingField label="Password"><Input type="password" value={draft.password} onChange={(event) => update('password', event.target.value)} placeholder="At least 8 characters" /></OnboardingField></div></Step>
  if (step === 1) return <Step title="Let’s start from the base... Your CV :-)" description="Accepted file types: PDF and DOC formats."><button type="button" onClick={onChooseFile} className="mt-7 flex w-full flex-col items-center rounded-xl border-2 border-dashed border-input-border bg-input p-10 text-center hover:border-accent"><Upload className="size-10 text-accent" /><strong className="mt-4 text-primary">{draft.resumeFile?.name || existingResumeName || 'Browse Files'}</strong><span className="mt-2 text-sm text-muted-foreground">Drag and Drop or choose a file</span></button>{/* Legacy signup verified the phone with Twilio Verify; the account exists from step 1 on, so it is offered here. */}{draft.phone.trim() ? <div className="mt-6 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border px-4 py-3 text-sm"><span className="text-muted-foreground">{phoneVerified ? `Phone ${draft.phone} verified.` : `Verify your phone number ${draft.phone} to receive job updates by SMS.`}</span>{phoneVerified ? null : <Button type="button" size="sm" variant="outline" onClick={() => setVerifyPhoneOpen(true)}>Verify phone</Button>}</div> : null}{verifyPhoneOpen ? <PhoneVerifyDialog phone={draft.phone.trim()} onClose={() => setVerifyPhoneOpen(false)} onVerified={() => { setPhoneVerified(true); setVerifyPhoneOpen(false) }} /> : null}</Step>
  if (step === 2) return <Step title="What works for you?" description="Availability, workspace, seniority, target roles and skills."><div className="mt-7"><JobPreferencesEditor value={draft.preferences} onChange={updatePreferences} section="about-you" availability={draft.availability} onAvailabilityChange={(value) => update('availability', value)} options={options} onLocationResultsSeen={onLocationResultsSeen} /></div></Step>
  if (step === 3) return <Step title="What kind of job, and where?" description="Job type, desired salary and desired location."><div className="mt-7"><JobPreferencesEditor value={draft.preferences} onChange={updatePreferences} section="about-job" availability={draft.availability} onAvailabilityChange={(value) => update('availability', value)} options={options} onLocationResultsSeen={onLocationResultsSeen} /></div></Step>
  if (step === 4) return <Step title="What kind of company?" description="Company size and industry — both optional."><div className="mt-7"><JobPreferencesEditor value={draft.preferences} onChange={updatePreferences} section="about-company" availability={draft.availability} onAvailabilityChange={(value) => update('availability', value)} options={options} onLocationResultsSeen={onLocationResultsSeen} /></div></Step>
  if (step === 5) {
    // A language already on the profile keeps the level set there (the wizard hydrated it); a new
    // pick writes level 1, the exact write legacy's own Languages step did for every selection —
    // proficiency is profile-page territory, the wizard only asks WHICH languages.
    const languagesFromLabels = (labels: string[]) =>
      labels.flatMap((label) => {
        const option = languageOptions.find((candidate) => candidate.label === label)
        if (!option) return []
        return [draft.languages.find((entry) => entry.language === Number(option.id)) ?? { language: Number(option.id), level: 1 }]
      })
    return <Step title="Which languages do you speak?" description="Select every language you can work in."><div className="mt-7"><SearchPicker options={languageOptions} values={languageLabels(draft.languages)} onChange={(next) => update('languages', languagesFromLabels(next))} placeholder="Search a language…" /></div></Step>
  }
  if (step === 6) return <Step title="Your LinkedIn profile address?"><label className="mt-7 grid gap-2 text-sm font-semibold text-[#555]">LinkedIn<Input type="url" value={draft.linkedIn} onChange={(event) => update('linkedIn', event.target.value)} placeholder="Enter LinkedIn URL" /></label></Step>
  if (step === 7) return <Step title="Enhance your profile" description="Optional — a short introduction and your skills sharpen the matches we recommend. You can always add them later from your profile."><div className="mt-7 grid gap-5"><OnboardingField label="About me"><textarea value={draft.aboutMe} onChange={(event) => update('aboutMe', event.target.value)} rows={5} placeholder="A few sentences about yourself" className="rounded-xl border border-input-border bg-input px-[15px] py-3 text-sm font-normal outline-none transition duration-200 placeholder:text-base placeholder:text-[#c7c7c7] focus:border-accent focus:placeholder:text-transparent" /></OnboardingField><ProfileSkillsEditor value={draft.profileSkills} onChange={(value) => update('profileSkills', value)} suggestions={skillSuggestions} /></div></Step>
  return <Step title="That’s it. We’re done. We’re on it" description="Review your selections, then build your profile."><div className="mt-7 grid gap-3 rounded-xl bg-input p-5 text-sm text-[#555]"><SummaryRow label="Name" value={`${draft.firstName} ${draft.lastName}`.trim()} /><SummaryRow label="Resume" value={draft.resumeFile?.name || existingResumeName} /><SummaryRow label="Status" value={availabilityOptions.find((item) => item.id === draft.availability)?.label ?? ''} /><SummaryRow label="Locations" value={draft.preferences.desiredLocations.join(', ')} /><SummaryRow label="Roles" value={draft.preferences.jobTitles.join(', ')} /><SummaryRow label="Skills" value={draft.preferences.skills.map((s) => s.skillName).join(', ')} /><SummaryRow label="Languages" value={languageLabels(draft.languages).join(', ')} /><SummaryRow label="Salary" value={draft.preferences.desiredSalary} /></div></Step>
}

function OnboardingField({ label, children }: { label: string; children: ReactNode }) {
  return <label className="grid gap-2 text-sm font-semibold text-[#555]">{label}{children}</label>
}

function Step({ title, description, children }: { title: string; description?: string; children?: ReactNode }) {
  return <><p className="text-sm font-semibold text-accent">Build your profile</p><h1 className="mt-2 text-3xl font-semibold tracking-tight text-primary">{title}</h1>{description ? <p className="mt-3 text-muted-foreground">{description}</p> : null}{children}</>
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return <div className="grid grid-cols-[100px_1fr] gap-3"><strong className="text-primary">{label}</strong><span>{value || 'Not provided'}</span></div>
}

function validateStep(
  step: number,
  draft: OnboardingDraft,
  context: { isAuthenticated: boolean; hasServerResume: boolean },
) {
  if (step === 0 && (!draft.firstName.trim() || !draft.lastName.trim() || !draft.email.trim() || !draft.phone.trim())) return 'First name, last name, email and phone are required.'
  if (step === 0 && !/^\S+@\S+\.\S+$/.test(draft.email)) return 'Enter a valid email address.'
  // Once the account exists the password's job is done and it is never stored — a Back-then-
  // Continue over step 0 must not demand it again.
  if (step === 0 && !context.isAuthenticated && draft.password.length < 8) return 'Password must be at least 8 characters.'
  if (step === 1 && !draft.resumeFile && !context.hasServerResume) return 'Please upload a resume to continue.'
  if (step === 2 && draft.preferences.seniorityLevels.length === 0) return 'You must select at least one seniority level.'
  if (step === 2 && draft.preferences.jobTitles.length === 0) return 'You must select at least one role.'
  if (step === 2 && draft.preferences.skills.length < 3) return 'You must select at least three skills.'
  if (step === 2 && draft.preferences.workspaceTypes.length === 0) return 'You must select at least one workspace type.'
  if (step === 3 && draft.preferences.jobTypes.length === 0) return 'You must select at least one job type.'
  if (step === 3 && draft.preferences.desiredLocations.length === 0) return 'You must select at least one location.'
  if (step === 3 && Number(draft.preferences.desiredSalary) < 5000) return 'Salary must be at least 5,000.'
  // Required, like legacy's own Languages step ('Required' on an empty selection). Step 7
  // (Enhance profile) has no rule here on purpose — the spec calls it "Optional enrichment".
  if (step === 5 && draft.languages.length === 0) return 'You must select at least one language.'
  if (step === 6 && !isLinkedInUrl(draft.linkedIn)) return 'Enter a valid LinkedIn profile URL.'
  if (step === 6 && draft.linkedIn.length > 1000) return 'LinkedIn URL must be 1,000 characters or fewer.'
  return ''
}

function isLinkedInUrl(value: string) {
  try {
    const url = new URL(value)
    return (url.protocol === 'https:' || url.protocol === 'http:')
      && (url.hostname === 'linkedin.com' || url.hostname.endsWith('.linkedin.com'))
  } catch {
    return false
  }
}

function readInitialStep(): number {
  try {
    const stored = Number(window.localStorage.getItem(STEP_KEY))
    return Number.isInteger(stored) ? Math.min(Math.max(stored, 0), steps.length - 1) : 0
  } catch {
    return 0
  }
}
